/**
 * Expanded body of one `email` row in the communication log: headers, the files
 * the email carried, and the message. Consumed by `CommunicationTable`'s
 * expandable row.
 *
 * A sent email carrying `bodyAttachment` (SYD-2697) reads that stored file from
 * Storage and renders it in full inside a sandboxed `srcdoc` iframe: scripts
 * never run, links open in a new tab, and the frame grows to its content's
 * height on load. Until the file arrives, or when it cannot be read, the row
 * shows the clipped plain-text `body`, which is all an entry logged before
 * SYD-2697 has.
 *
 * The copy the record embeds carries the whole preview identity (fileUid key,
 * currentVersionId, version type) plus each name and size, so nothing is
 * re-fetched. Labels are the exception: they come from the live artifact map
 * through `useCurrentArtifact`, joined on the file identifier, and the copy is
 * neither read for them nor written with them — a year-old email shows today's
 * labels, because snapshotting them per entry would make every rename a
 * historical rewrite. A file the live map no longer holds keeps its name and its
 * preview, which reports for itself if the bytes are gone. Legacy entries
 * carrying only `outboundAttachmentLabels` have no stored blob and stay text.
 * Its overlay is a `communicationLog` host, so Extract schedule there is
 * disabled and points the reader at the form's Attachments.
 *
 * "No longer in the Attachments panel" is claimed only against a resolved
 * artifact. Outside an artifact scope, and while `data` is `null` — loading, or
 * the document store between disconnect and reconnect — the row says nothing
 * about a file at all: an artifact that has not answered yet is not evidence
 * that it dropped the file, and an expanded row would otherwise flip every file
 * to "removed" and back on each re-subscribe. A resolved artifact carrying no
 * `attachments` map does hold that evidence, and reads as empty.
 */
import { FC, useCallback, useEffect, useState } from 'react';
import { Button, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { getBytes, ref } from 'firebase/storage';
import { EmailCommunicationRecord } from '#types';
import AttachmentPreviewOverlay from '@/components/rendering/attachments/AttachmentPreviewOverlay';
import { AttachmentFileTypeIcon } from '@/components/rendering/attachments/AttachmentFileTypeIcon';
import { LabelTags } from '@/components/atoms/LabelTags';
import { useAttachmentLabels } from '@/use/useOrganizationParameters';
import { useCurrentArtifact } from '@/use/useCurrentArtifact';
import { storage } from '@/services/startStorage';
import FlexBox from '../../../../atoms/FlexBox';
import FlexCol from '../../../../atoms/FlexCol';

const { Text } = Typography;

interface EmailExpandedContentProps {
  record: EmailCommunicationRecord;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
};

const EmailExpandedContent: FC<EmailExpandedContentProps> = ({ record }) => {
  const artifact = useCurrentArtifact();
  const liveAttachments =
    artifact.kind === 'none' || !artifact.data
      ? undefined
      : (artifact.data.attachments ?? {});
  const attachmentLabels = useAttachmentLabels();

  const [previewTarget, setPreviewTarget] = useState<{
    fileUid: string;
    versionId: string;
    fileName: string;
    declaredContentType?: string;
  } | null>(null);

  const closePreview = useCallback(() => {
    setPreviewTarget(null);
  }, []);

  const bodyPath = record.bodyAttachment?.storagePath;
  const [sentCopy, setSentCopy] = useState<{ path: string; html: string } | null>(null);
  const [sentCopyHeight, setSentCopyHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (!bodyPath) return undefined;
    const subscription = { active: true };
    getBytes(ref(storage, bodyPath))
      .then((bytes) => {
        if (subscription.active) setSentCopy({ path: bodyPath, html: new TextDecoder().decode(bytes) });
      })
      .catch((error: unknown) => {
        console.warn('Failed to read the sent email body:', error);
      });
    return () => {
      subscription.active = false;
    };
  }, [bodyPath]);

  const sentHtml = sentCopy && sentCopy.path === bodyPath ? sentCopy.html : undefined;

  return (
    <FlexCol
      style={{
        maxHeight: '50vh',
        overflow: 'hidden auto',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        width: '100%',
        maxWidth: '100%',
      }}
      gap={0}
    >
      <Text>
        <Text strong>From </Text>
        {record.from}
      </Text>
      <Text>
        <Text strong>To </Text>
        {record.to.join(', ')}
      </Text>
      {record.cc && record.cc.length > 0 && (
        <Text>
          <Text strong>CC </Text>
          {record.cc.join(', ')}
        </Text>
      )}
      {record.bcc && record.bcc.length > 0 && (
        <Text>
          <Text strong>BCC </Text>
          {record.bcc.join(', ')}
        </Text>
      )}
      <Text>
        <Text strong>Subject </Text>
        {record.subject}
      </Text>
      <Text>
        <Text strong>Date </Text>
        {dayjs(record.timestamp.toDate()).format('YYYY-MM-DD HH:mm')}
      </Text>
      {record.outboundAttachmentLabels &&
        record.outboundAttachmentLabels.length > 0 && (
          <Text>
            <Text strong>Attachments (sent)&nbsp;</Text>
            {record.outboundAttachmentLabels.map((name, index) => (
              <Text key={`${name}-${index}`}>
                {name}
                {'  '}
              </Text>
            ))}
          </Text>
        )}
      {record.attachments && Object.keys(record.attachments).length > 0 && (
        <FlexCol gap={5} noGrow>
          <Text strong>Attachments</Text>
          {Object.entries(record.attachments).map(
            ([attachmentId, attachment], index) => {
              const versionId =
                attachment.currentVersionId &&
                attachment.versions?.[attachment.currentVersionId]
                  ? attachment.currentVersionId
                  : Object.keys(attachment.versions ?? {})[0];
              const currentVersion = versionId
                ? attachment.versions?.[versionId]
                : undefined;

              if (!versionId || !currentVersion) return null;

              const liveEntry = liveAttachments?.[attachmentId];

              return (
                <FlexBox key={attachmentId} noGrow justifyStart wrap gap={5}>
                  <AttachmentFileTypeIcon
                    fileName={currentVersion.versionFileName}
                    declaredContentType={currentVersion.type}
                  />
                  <Button
                    type="link"
                    size="small"
                    style={{ paddingInline: 0, height: 'auto' }}
                    id={`email-attachment-preview-${attachmentId}`}
                    aria-label={`Preview attachment ${index + 1}: ${currentVersion.versionFileName}`}
                    onClick={() =>
                      setPreviewTarget({
                        fileUid: attachmentId,
                        versionId,
                        fileName: currentVersion.versionFileName,
                        declaredContentType: currentVersion.type,
                      })
                    }
                  >
                    {currentVersion.versionFileName}
                  </Button>
                  <Text type="secondary">
                    ({formatFileSize(currentVersion.sizeBytes)})
                  </Text>
                  {liveAttachments &&
                    (liveEntry ? (
                      <LabelTags
                        set={attachmentLabels}
                        labelIds={liveEntry.labelIds}
                      />
                    ) : (
                      <Tag
                        aria-label={`${currentVersion.versionFileName} is no longer in the Attachments panel`}
                      >
                        Not in Attachments
                      </Tag>
                    ))}
                </FlexBox>
              );
            },
          )}
        </FlexCol>
      )}
      {sentHtml ? (
        <iframe
          title={`Sent email: ${record.subject}`}
          aria-label={`Sent email ${record.id}`}
          sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          srcDoc={`<base target="_blank">${sentHtml}`}
          onLoad={(event) => setSentCopyHeight(
            event.currentTarget.contentDocument?.documentElement.scrollHeight,
          )}
          style={{ width: '100%', height: sentCopyHeight, border: 'none' }}
        />
      ) : record.body && (
        <Typography.Paragraph
          style={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          <small>{record.body}</small>
        </Typography.Paragraph>
      )}
      <AttachmentPreviewOverlay
        open={Boolean(previewTarget)}
        extractScheduleHost={{ kind: 'communicationLog' }}
        onClose={closePreview}
        fileUid={previewTarget?.fileUid}
        versionId={previewTarget?.versionId}
        fileName={previewTarget?.fileName}
        declaredContentType={previewTarget?.declaredContentType}
      />
    </FlexCol>
  );
};

export default EmailExpandedContent;
